import React, { useEffect, useState } from 'react';
import {
  Send,
  Link,
  Unlink,
  Copy,
  ExternalLink,
  CheckCircle,
  XCircle,
  Loader2,
  RefreshCw,
  Info,
} from 'lucide-react';
import { clsx } from 'clsx';
import { useTranslation } from '@/i18n';
import { toast } from '@/stores/toastStore';
import {
  apiClient,
  TelegramLinkStatusResponse,
  TelegramTokenGenerateResponse,
  TelegramBotInfoResponse,
} from '@/utils/apiClient';

export const TelegramSettings: React.FC = () => {
  const { t } = useTranslation();
  const tgT = (t as any).telegram;

  const [status, setStatus] = useState<TelegramLinkStatusResponse | null>(null);
  const [botInfo, setBotInfo] = useState<TelegramBotInfoResponse | null>(null);
  const [token, setToken] = useState<TelegramTokenGenerateResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [unlinking, setUnlinking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [statusRes, botRes] = await Promise.all([
        apiClient.getTelegramStatus(),
        apiClient.getTelegramBotInfo(),
      ]);
      setStatus(statusRes);
      setBotInfo(botRes);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load Telegram settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleGenerateToken = async () => {
    setGenerating(true);
    setError(null);
    try {
      const res = await apiClient.generateTelegramToken();
      setToken(res);
      // Refresh status
      const statusRes = await apiClient.getTelegramStatus();
      setStatus(statusRes);
      toast.success('Telegram token generated');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to generate token';
      setError(msg);
      toast.error(msg);
    } finally {
      setGenerating(false);
    }
  };

  const handleUnlink = async () => {
    setUnlinking(true);
    setError(null);
    try {
      await apiClient.unlinkTelegram();
      setToken(null);
      const statusRes = await apiClient.getTelegramStatus();
      setStatus(statusRes);
      toast.success('Telegram unlinked');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to unlink';
      setError(msg);
      toast.error(msg);
    } finally {
      setUnlinking(false);
    }
  };

  const handleCopyToken = () => {
    if (token?.access_token) {
      navigator.clipboard.writeText(token.access_token);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success(t.chat.copiedToClipboard);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Send className="h-6 w-6 text-blue-500" />
            {tgT?.title ?? 'Telegram Integration'}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {tgT?.description ?? 'Link your Telegram account to receive notifications and chat with the AI via Telegram'}
          </p>
        </div>
        <button
          onClick={fetchData}
          className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          title={t.common.refresh}
        >
          <RefreshCw className="h-5 w-5" />
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
          <span className="text-sm text-red-700 dark:text-red-400">{error}</span>
        </div>
      )}

      {/* Bot status */}
      {botInfo && !botInfo.is_configured && (
        <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md flex items-start gap-2">
          <Info className="h-5 w-5 text-yellow-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-yellow-800 dark:text-yellow-300">
              {tgT?.botNotConfigured ?? 'Telegram Bot Not Configured'}
            </p>
            <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">
              {tgT?.botNotConfiguredHint ?? 'Set the TELEGRAM_BOT_TOKEN environment variable to enable the Telegram bot.'}
            </p>
          </div>
        </div>
      )}

      {/* Status Card */}
      <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg p-5 mb-6">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-3">
          {tgT?.status ?? 'Connection Status'}
        </h3>

        <div className="flex items-center gap-3 mb-4">
          {status?.is_linked && status?.is_active ? (
            <>
              <CheckCircle className="h-8 w-8 text-green-500" />
              <div>
                <p className="font-medium text-green-700 dark:text-green-400">
                  {tgT?.linked ?? 'Linked'}
                </p>
                {status.telegram_username && (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    @{status.telegram_username}
                  </p>
                )}
              </div>
            </>
          ) : (
            <>
              <XCircle className="h-8 w-8 text-gray-400" />
              <div>
                <p className="font-medium text-gray-600 dark:text-gray-300">
                  {tgT?.notLinked ?? 'Not Linked'}
                </p>
                <p className="text-sm text-gray-400">
                  {tgT?.notLinkedHint ?? 'Generate a token to link your account'}
                </p>
              </div>
            </>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3">
          {status?.is_linked && status?.is_active ? (
            <button
              onClick={handleUnlink}
              disabled={unlinking}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-700 bg-red-50 dark:bg-red-900/30 dark:text-red-400 border border-red-200 dark:border-red-800 rounded-md hover:bg-red-100 dark:hover:bg-red-900/50 disabled:opacity-50"
            >
              {unlinking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Unlink className="h-4 w-4" />}
              {tgT?.unlink ?? 'Unlink Account'}
            </button>
          ) : (
            <button
              onClick={handleGenerateToken}
              disabled={generating || !botInfo?.is_configured}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link className="h-4 w-4" />}
              {tgT?.generateToken ?? 'Generate Link Token'}
            </button>
          )}
        </div>
      </div>

      {/* Token display (after generation) */}
      {token && !status?.is_linked && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-5 mb-6">
          <h3 className="text-sm font-semibold text-blue-800 dark:text-blue-300 mb-3">
            {tgT?.linkInstructions ?? 'Link Instructions'}
          </h3>

          {/* QR Code — primary linking method */}
          {token.qr_code_data_url && (
            <div className="flex flex-col items-center mb-5">
              <div className="bg-white p-3 rounded-xl shadow-md mb-2">
                <img
                  src={token.qr_code_data_url}
                  alt="Scan to link Telegram"
                  className="w-48 h-48"
                />
              </div>
              <p className="text-sm text-blue-700 dark:text-blue-400 text-center">
                {tgT?.scanQr ?? 'Scan with your phone camera to link instantly'}
              </p>
            </div>
          )}

          <div className="flex items-center gap-3 mb-3">
            <div className="flex-1 h-px bg-blue-200 dark:bg-blue-700" />
            <span className="text-xs text-blue-400 dark:text-blue-500 uppercase tracking-wide">
              {tgT?.orManually ?? 'or manually'}
            </span>
            <div className="flex-1 h-px bg-blue-200 dark:bg-blue-700" />
          </div>

          {/* Manual token */}
          <div className="flex items-center gap-2 mb-3">
            <code className="flex-1 px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded text-sm font-mono text-gray-900 dark:text-white select-all truncate">
              /link {token.access_token}
            </code>
            <button
              onClick={handleCopyToken}
              className={clsx(
                'p-2 rounded-md transition-colors flex-shrink-0',
                copied
                  ? 'text-green-600 bg-green-50 dark:bg-green-900/30'
                  : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700'
              )}
              title={copied ? 'Copied!' : 'Copy command'}
            >
              {copied ? <CheckCircle className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            </button>
          </div>

          <p className="text-sm text-blue-700 dark:text-blue-400 mb-3">
            {token.instructions}
          </p>

          {/* Deep link button */}
          {token.deep_link && (
            <a
              href={token.deep_link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#2AABEE] rounded-md hover:bg-[#229ED9]"
            >
              <Send className="h-4 w-4" />
              {tgT?.openInTelegram ?? 'Open in Telegram'}
              <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
      )}

      {/* Info note */}
      <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
        <div className="flex items-start gap-2">
          <Info className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-gray-600 dark:text-gray-400">
            <p className="font-medium mb-1">{tgT?.howItWorks ?? 'How it works'}</p>
            <ul className="list-disc list-inside space-y-1">
              <li>{tgT?.feature1 ?? 'Messages sent to the bot are processed as your authenticated queries'}</li>
              <li>{tgT?.feature2 ?? 'Scheduled query results can be sent to Telegram when notification is set to "Telegram"'}</li>
              <li>{tgT?.feature3 ?? 'Your account stays secure — the bot verifies your identity through the link token'}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};
